import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The sun through the window is the only
// light; a stage wash over the photograph would turn the room into a set.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'sunroom',
  family: 'photographic',
  danceability: 0.10,
  energyLevel: 0.10,
  bpmMin: 40,
  bpmMax: 120,
  movement: 0.10,
  tonality: 0.95,
  prefers: { dynamics: [0.0, 0.6], percussiveness: [0.0, 0.35] },
  requiresTier: 0,
  enabled: true
};

// Sunroom: an empty room at first light, where the rectangle of sun thrown
// by the window slides over the floor and up the wall for the length of the
// track. Ambient, solo piano, classical, folk, anything contemplative.
//
// THE PHOTO IS THE ROOM, AND THE LIGHT IS COMPUTED ON IT. sunroom.jpeg (a
// shaded room, a six-pane sash window at the left, bare boards, a chair)
// is a full-frame quad. For every pixel the fragment shader finds the 3D
// point of the surface it shows - the floor, the back wall, the right wall,
// the ceiling, as PLANES measured on the photograph (tools/build-scenes.mjs
// carries the measurement and the manifest carries the numbers) - then
// casts a ray from that point toward the sun: if the ray crosses one of the
// window's panes, the pixel is in the light. Nothing is pasted: the mullion
// and the rails cast their own shadows, and the shape stretches and shears
// as the sun moves, exactly as a projection must.
//
// THE SUN IS THE CLOCK. Its direction follows the track's progress along a
// low path: at the start a grazing, rosy light lies along the left wall by
// the window; through the middle a long gold band crosses the boards; at
// the end it climbs the back wall - over the cover, which is meant - and
// fades. On a web radio with no duration, director.js's synthetic progress
// (a 210 s loop from the last title change) drives it instead.
//
// THE PENUMBRA IS WHAT MAKES IT PHYSICAL. The sun has a width: an edge is
// crisp where the floor is close to the window and soft far from it, so the
// softening grows with the distance the ray travelled (tier 2; a fixed width
// at tiers 1 and 0). A thin halo rides outside the edge, the wall and the
// ceiling facing the patch take a little of its colour back (tier 2), and
// dust drifts in the beam - visible there and nowhere else, since each mote
// is PLACED in the beam: a point on the window plane and a distance along
// the ray, not a point in the room tested against the light.
//
// THE DEPTH MAP (Depth Anything, recalibrated on the floor and blended with
// this very box by tools/depth-scene.py) is not what the light is computed
// on - the planes are exact, the network is not - but it is what hides a
// mote behind the chair, and `?sunroomdepth=1` shows it.
//
// NOTHING MOVES FAST ENOUGH TO BE NOTICED. No pulse, no beat. The voile
// breathes with bands.mid at three pixels of amplitude, the dust twinkles
// with bands.high, a cloud passes every minute or two and that is all.

// --- the room, in the manifest's units ----------------------------------------
// Camera at the origin, +x right, +y up, +z into the room; the camera stands
// hc = 1 - vanish.y above the floor and the bottom of the frame is at depth
// 1 (depth-scene.py's convention). Fallback meta identical to the build's;
// the manifest wins.
const FALLBACK_META = {
  vanish: { x: 0.73, y: 0.665 },
  room: { left: -6.5, right: 1.5, back: 10.0, ceiling: 5.2 },
  window: {
    corners: [[0.2086, 0.0612], [0.3147, 0.1602], [0.3147, 0.5456], [0.2086, 0.5221]],
    mullion: { u: 0.266, widthPx: 7 }, rail: { frac: 0.51, thick: 0.05 }, topRow: 0.6, barThick: 0.05
  },
  curtain: { u0: 0.323, u1: 0.385, v1: 0.705, inset: 0.5, z0: 8.6, z1: 10.1, transmission: 0.45 },
  sourceSize: [1376, 768]
};
const SCENE_MANIFEST = 'textures/sunroom.json';
const W_MAX_DEFAULT = 1.25;
const RADIO_LOOP_SECONDS = 210;   // director.js's syntheticProgress

// --- the sun's path -------------------------------------------------------------
// Keyframes over progress: azimuth (degrees; the travel of the light, 0 =
// straight across the room from the window, positive = toward the lens,
// negative = toward the back wall), elevation (degrees above the horizon),
// level. Chosen on the measured room so that the patch is where the brief
// puts it AND in the frame: this camera lies on the floor, so the boards
// only have height near the lens, and a patch beyond z 4 is a sliver along
// the horizon. The first version opened on that sliver (a faint dawn under
// the window) and the first fifteen seconds looked static: the light is
// now INSTALLED from the first frame - the long band across the near boards,
// at full level, warmed rose for the first sixth - and both angles move
// from p 0, since on the near boards a degree of either is what shows
// (measured: the band's centroid travels ~130 px in the first fifteen
// seconds of a four-minute track at 1080p). Then the far floor and the
// right wall around 0.5-0.6, the back wall from 0.7 - over the cover, which
// is meant - and the fade at 1.
const SUN_PATH = [
  // p,    az,  el, level
  [0.00, 49.0, 22.0, 1.00],
  [0.12, 44.0, 19.0, 1.00],
  [0.26, 34.0, 16.0, 1.00],
  [0.42, 20.0, 13.0, 1.00],
  [0.55, 3.0, 12.0, 0.95],
  [0.66, -16.0, 14.0, 0.90],
  [0.78, -31.0, 19.0, 0.85],
  [0.90, -43.0, 23.0, 0.75],
  [1.00, -52.0, 26.0, 0.15]
];
// Colours, linear (the chain is ACES + sRGB): dawn, noon, evening.
const SUN_DAWN = [1.00, 0.50, 0.40];
const SUN_GOLD = [1.00, 0.80, 0.52];
const SUN_LATE = [1.00, 0.70, 0.50];
const PALETTE_TINT = 0.20;        // the brief: palette -> hue of the light, 20 %
// The light. "Luminance 0.85 on the floor" is a percentage of the OUTPUT;
// calibrated in the harness against the photo's floor through the chain.
const SUN_GAIN = 2.0;
const COS_REF = 0.40;             // the incidence the gain is stated at (sin 24 deg)
// THE SHOULDER. A white wall in full sun is five times brighter than the
// shaded boards, which no screen shows; and the chain's bloom (threshold
// 0.78 linear, strength 0.7) turns any large area above it into fog - the
// first frames were a white haze over the whole floor. So the frame goes
// through a soft shoulder before the composer: identity below SHOULDER_A,
// a rational approach to SHOULDER_M above it (unit slope at the join). The asymptote sits just
// under the bloom threshold: nothing blooms, not even the sky in the
// window. "Luminance 0.85 on the floor" is a share of the OUTPUT: through
// ACES at exposure 1.1 and the sRGB curve, 0.68 linear lands at 217/255,
// which is where the asymptote is set (measured: the lit boards' p95).
const SHOULDER_A = 0.40;
const SHOULDER_M = 0.70;
const HALO_GAIN = 0.06;
const HALO_PX = 30.0;
const PENUMBRA_K = 0.005;         // half-width per unit of ray length (the real sun's 0.0046, near enough)
const PENUMBRA_FIXED = 0.06;      // tiers 1 and 0
const PENUMBRA_MIN = 0.012;
const BOUNCE_GAIN = 0.36;         // tier 2: the wall facing the patch gains ~8 %
const FLOOR_TINT = [0.62, 0.42, 0.26];
const WALL_TINT = [0.92, 0.90, 0.86];
const AMBIENT = 0.92;             // the photo's own exposure, a touch under
// Clouds: every 60-120 s, the level drops to 40 % for 4-8 s and the edges blur.
const CLOUD_GAP = [60, 120];
const CLOUD_HOLD = [4, 8];
const CLOUD_RAMP = 2.5;
const CLOUD_LEVEL = 0.40;
const CLOUD_BLUR = 3.0;           // penumbra x (1 + CLOUD_BLUR * cloud)
// The voile and the dust.
const VEIL_PX = 3.0;
const DUST_SIZE_PX = 3.2;         // at depth 1 on a 720-line buffer
const DUST_GAIN = 0.75;
const PROGRESS_TAU = 1.5;         // a seek glides, nothing jumps

const TIER = {
  0: { penumbra: 'fixed', dust: 0, veil: false, bounce: false },
  1: { penumbra: 'fixed', dust: 400, veil: false, bounce: false },
  2: { penumbra: 'variable', dust: 400, veil: true, bounce: true }
};
const DEFAULT_TIER = 2;
const SAFE_FLOOR = 0.60;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
// Catmull-Rom over the keyframes' columns, clamped at the ends.
function pathAt(p, col) {
  const n = SUN_PATH.length;
  let i = 0;
  while (i < n - 2 && p > SUN_PATH[i + 1][0]) i++;
  const k0 = SUN_PATH[Math.max(0, i - 1)], k1 = SUN_PATH[i], k2 = SUN_PATH[i + 1], k3 = SUN_PATH[Math.min(n - 1, i + 2)];
  const t = clamp01((p - k1[0]) / Math.max(1e-6, k2[0] - k1[0]));
  const a = k0[col], b = k1[col], c = k2[col], d = k3[col];
  return 0.5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t * t + (-a + 3 * b - 3 * c + d) * t * t * t);
}

// =============================================================================
// SHADERS
// =============================================================================

// The photo's camera, shared by every shader here.
//   uProj = (vanish.x, vanish.y, photo aspect, hc)   hc = 1 - vanish.y
//   uFit  = (sx, sy): the share of the photo the frame shows (cover fit)
// Image coordinates are (u, v), v DOWN, as the manifest measures them.
const CAMERA_CHUNK = `
  uniform vec4 uProj;
  uniform vec2 uFit;
  vec2 imageOf(vec3 P) { return vec2(uProj.x + P.x / (uProj.z * P.z), uProj.y - P.y / P.z); }
  vec2 ndcOf(vec2 img) { return vec2((img.x - 0.5) * 2.0 / uFit.x, (0.5 - img.y) * 2.0 / uFit.y); }
  vec2 imageOfNdc(vec2 ndc) { return vec2(0.5 + ndc.x * 0.5 * uFit.x, 0.5 - ndc.y * 0.5 * uFit.y); }
  vec3 rayOf(vec2 img) { return vec3((img.x - uProj.x) * uProj.z, uProj.y - img.y, 1.0); }
`;
// The depth map: proximity w = 1 / z, 16 bits split over R (high) and G (low).
const DEPTH_CHUNK = `
  uniform sampler2D uDepth;
  uniform float uWMax;
  float depthAt(vec2 img) {
    vec2 rg = texture2D(uDepth, vec2(img.x, 1.0 - img.y)).rg;
    return (256.0 * rg.x + rg.y) * (uWMax / 257.0);
  }
`;
// THE PROJECTION. From a point P of the room, the ray toward the sun meets
// the window's wall at H; the pixel is lit by as much of the pane grid as
// sits under H, each edge softened by pw (the penumbra, in room units).
// Two columns and three rows, so the coverage is a product of two sums.
const WINDOW_CHUNK = `
  uniform vec3 uSun;            // unit vector toward the sun, x < 0
  uniform float uWinX;          // the window's wall
  uniform vec4 uWinZ;           // column 1: x..y, column 2: z..w
  uniform vec4 uWinY;           // row 1: x..y, row 2: z..w
  uniform vec2 uWinY2;          // row 3
  uniform vec2 uPenumbra;       // per unit of ray length, floor
  uniform vec4 uCurtain;        // its plane x, z0, z1, transmission
  uniform float uWave;          // the voile's sway on its shadow's edge, room units
  uniform float uTime;
  // A soft interval [a, b] with edges of half-width w, written with the
  // reciprocal 0.5 / w so that a frame costs no division per edge: the Pi
  // paid ~50 ns a pixel for the first version, and twenty smoothsteps'
  // divisions were most of it.
  float box(float v, float a, float b, float inv) {
    float s0 = clamp((v - a) * inv + 0.5, 0.0, 1.0);
    float s1 = clamp((b - v) * inv + 0.5, 0.0, 1.0);
    return s0 * s0 * (3.0 - 2.0 * s0) * s1 * s1 * (3.0 - 2.0 * s1);
  }
  float grid(vec3 H, float inv) {
    float c = box(H.z, uWinZ.x, uWinZ.y, inv) + box(H.z, uWinZ.z, uWinZ.w, inv);
    float r = box(H.y, uWinY.x, uWinY.y, inv) + box(H.y, uWinY.z, uWinY.w, inv) + box(H.y, uWinY2.x, uWinY2.y, inv);
    return min(c, 1.0) * min(r, 1.0);
  }
  // x: the pane coverage at P, y: the same with the edges widened by
  // extra (the halo). One ray, one hit, two widths.
  vec2 coverage2(vec3 P, float extra) {
    float t = (uWinX - P.x) / uSun.x;
    if (t <= 0.0) return vec2(0.0);
    vec3 H = P + uSun * t;
    float pw = max(uPenumbra.y, uPenumbra.x * t);
    return vec2(grid(H, 0.5 / pw), grid(H, 0.5 / (pw + extra)));
  }
  // The voile hangs inside the wall, from the glass's right edge: a ray that
  // crosses it on its way in is dimmed, behind a wavy edge.
  float curtain(vec3 P, float extra) {
    float tc = (uCurtain.x - P.x) / uSun.x;
    if (tc <= 0.0) return 1.0;
    vec3 Hc = P + uSun * tc;
    float edge = uCurtain.y + uWave * sin(Hc.y * 2.3 + uTime * 0.30) * sin(Hc.y * 0.9 - uTime * 0.17);
    float pw = max(uPenumbra.y, uPenumbra.x * tc) + 0.05 + extra;
    float inside = box(Hc.z, edge, uCurtain.z, 0.5 / pw);
    return 1.0 - inside * (1.0 - uCurtain.w);
  }
`;

const photoVertexShader = `
  precision mediump float;
  varying vec2 vNdc;
  void main() {
    vNdc = position.xy * 2.0;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function photoFragmentShader(layout) {
  return `
  precision mediump float;
  ${CAMERA_CHUNK}
  ${DEPTH_CHUNK}
  ${WINDOW_CHUNK}
  uniform sampler2D uMap;
  uniform vec4 uRoom;           // left, right, back, ceiling (above the camera)
  uniform float uAmbient;
  uniform vec3 uSunColor;
  uniform float uSunLevel;
  uniform vec4 uBounce;         // the patch's centre xyz, level
  uniform vec3 uBounceColor;
  uniform vec3 uCurtainUV;      // u0, u1, v1 of the voile in the photo
  uniform float uVeil;          // its sway, in image-height units
  uniform float uPixelH;        // image-height units per screen pixel
  uniform float uDebug;         // 2: the depth map
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vNdc;

  float safeAt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.32, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${SAFE_FLOOR.toFixed(2)} + (att - 0.32) * (${((1 - SAFE_FLOOR) / 0.68).toFixed(5)});
  }
  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    vec2 img = imageOfNdc(vNdc);

    // THE VOILE: the photo's own curtain, its texels nudged by a slow noise
    // over its region - three pixels at most, so it breathes rather than
    // blows.
    ${layout.veil ? `
    {
      float m = smoothstep(uCurtainUV.x - 0.012, uCurtainUV.x + 0.012, img.x)
              * (1.0 - smoothstep(uCurtainUV.y - 0.012, uCurtainUV.y + 0.012, img.x))
              * (1.0 - smoothstep(uCurtainUV.z - 0.03, uCurtainUV.z, img.y));
      if (m > 0.001) {
        float n = sin(img.y * 23.0 + uTime * 0.31) * sin(img.y * 9.0 - uTime * 0.19);
        img += vec2(n, 0.3 * sin(img.y * 15.0 + uTime * 0.23)) * (uVeil * m);
      }
    }` : ''}
    vec2 tc = vec2(img.x, 1.0 - img.y);
    vec3 ph = texture2D(uMap, tc).rgb;

    // THE SURFACE UNDER THE PIXEL: the nearest of the box's planes along
    // the pixel's ray (the back wall unless the floor, a side wall or the
    // ceiling comes first).
    vec3 r = rayOf(img);
    float t = uRoom.z;
    vec3 N = vec3(0.0, 0.0, -1.0);
    if (r.y < 0.0) { float tf = -uProj.w / r.y; if (tf < t) { t = tf; N = vec3(0.0, 1.0, 0.0); } }
    else if (r.y > 0.0) { float tcl = uRoom.w / r.y; if (tcl < t) { t = tcl; N = vec3(0.0, -1.0, 0.0); } }
    if (r.x < 0.0) { float tl = uRoom.x / r.x; if (tl < t) { t = tl; N = vec3(1.0, 0.0, 0.0); } }
    else if (r.x > 0.0) { float tr = uRoom.y / r.x; if (tr < t) { t = tr; N = vec3(-1.0, 0.0, 0.0); } }
    vec3 P = r * t;

    // THE LIGHT: the window's shape, cast from P toward the sun, on a face
    // the sun can see; a thin halo outside its edge.
    float cosI = dot(N, uSun);
    vec3 light = vec3(0.0);
    vec3 sun = vec3(0.0);
    if (cosI > 0.0 && uSunLevel > 0.001) {
      vec2 cv = coverage2(P, ${HALO_PX.toFixed(1)} * uPixelH * P.z);
      if (cv.y > 0.001) {   // the voile's shadow is only asked of pixels the light reaches
        float cf = ${layout.veil ? 'curtain(P, 0.0)' : '1.0'};
        float face = mix(1.0, cosI / ${COS_REF.toFixed(2)}, 0.5);
        sun = ph * uSunColor * (uSunLevel * face * cf * (cv.x + ${HALO_GAIN.toFixed(3)} * cv.y));
      }
    }
    // THE BOUNCE: what faces the patch takes a little of its colour back.
    ${layout.bounce ? `
    if (N.y < 0.5 && uBounce.w > 0.001) {
      vec3 d = uBounce.xyz - P;
      float dist = max(length(d), 0.3);
      float cosB = max(dot(N, d / dist), 0.0);
      light += uBounceColor * (uBounce.w * cosB / (1.0 + dist * dist * 0.08));
    }` : ''}

    vec3 col = ph * (uAmbient + light) + sun;
    {
      float l = dot(col, vec3(0.2125, 0.7154, 0.0721));
      if (l > ${SHOULDER_A.toFixed(2)}) {
        float x = l - ${SHOULDER_A.toFixed(2)};
        float m = ${SHOULDER_A.toFixed(2)} + ${(SHOULDER_M - SHOULDER_A).toFixed(2)} * x / (x + ${(SHOULDER_M - SHOULDER_A).toFixed(2)});
        col *= m / l;
      }
    }
    if (uDebug > 1.5) col = vec3(depthAt(imageOfNdc(vNdc)) / uWMax);

    col *= safeAt(vNdc);
    vec2 fcw = mod(floor(gl_FragCoord.xy), 64.0);
    col *= 1.0 + (hash12(fcw + fract(uTime * 3.0) * 7.0) - 0.5) * 0.03;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;
}

// THE DUST lives in the beam by construction: a mote is a point on the
// window plane and a fraction of the way along the ray from there to where
// the ray leaves the room. It carries the pane grid's own shadow, drifts
// very slowly, and twinkles with the treble.
const dustVertexShader = `
  precision mediump float;
  ${CAMERA_CHUNK}
  ${DEPTH_CHUNK}
  ${WINDOW_CHUNK}
  attribute vec4 aSeed;
  uniform vec4 uRoom;
  uniform float uPixelScale;
  uniform float uTwinkle;
  uniform float uSunLevel;
  varying float vA;
  void main() {
    float zw = mix(uWinZ.x, uWinZ.w, fract(aSeed.x + 0.012 * sin(uTime * 0.05 + aSeed.w * 6.283)));
    float yw = mix(uWinY.x, uWinY2.y, fract(aSeed.y + 0.015 * sin(uTime * 0.043 + aSeed.w * 4.1)));
    vec3 W = vec3(uWinX, yw, zw);
    float tHit = (W.y + uProj.w) / max(uSun.y, 0.02);
    if (uSun.z < -0.001) tHit = min(tHit, (W.z - uRoom.z) / uSun.z);
    if (uSun.z > 0.001) tHit = min(tHit, (W.z - 0.6) / uSun.z);
    tHit = min(tHit, (W.x - uRoom.y) / uSun.x);
    float tf = fract(aSeed.z + uTime * 0.004 * (0.5 + aSeed.w));
    vec3 P = W - uSun * (tf * tHit);
    // the grid's shadow, measured at the window plane itself (t = 0 there)
    float lit = grid(W, 25.0) * curtain(P, 0.0);
    float tw = 0.5 + 0.5 * sin(uTime * (1.3 + 1.1 * aSeed.w) + aSeed.z * 6.283);
    float twinkle = mix(0.55, 1.0, tw * uTwinkle) * (0.5 + 0.5 * aSeed.y);
    vec2 img = imageOf(P);
    float wScene = depthAt(img);
    float behind = smoothstep(0.0, 0.03, wScene - 1.0 / max(P.z, 0.05));
    float edge = smoothstep(0.0, 0.08, tf) * (1.0 - smoothstep(0.92, 1.0, tf));
    vA = lit * (1.0 - behind) * uSunLevel * twinkle * edge * ${DUST_GAIN.toFixed(2)};
    vec2 ndc = ndcOf(img);
    gl_Position = vec4(ndc * P.z, 0.0, P.z);
    gl_PointSize = clamp(${DUST_SIZE_PX.toFixed(1)} * uPixelScale / P.z * (0.7 + 0.6 * aSeed.x), 1.0, 4.0);
  }
`;
const dustFragmentShader = `
  precision mediump float;
  uniform vec3 uSunColor;
  varying float vA;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r2 = dot(c, c);
    if (r2 > 0.25) discard;
    float k = exp(-r2 * 12.0);
    gl_FragColor = vec4(uSunColor * (vA * k), 1.0);
  }
`;

// `?sunroomplanes=1`: the box and the window as wire, projected by the same
// camera. Each line must lie on its edge in the photograph.
const lineVertexShader = `
  precision mediump float;
  ${CAMERA_CHUNK}
  attribute vec3 aColor;
  varying vec3 vColor;
  void main() {
    vColor = aColor;
    vec3 P = position;
    vec2 ndc = ndcOf(imageOf(P));
    gl_Position = vec4(ndc * P.z, 0.0, P.z);
  }
`;
const lineFragmentShader = `
  precision mediump float;
  varying vec3 vColor;
  void main() { gl_FragColor = vec4(vColor, 1.0); }
`;

// =============================================================================

// Materials and their uniform holders are cached at module level: the Pi
// compiles a fragment shader of this size in seconds, and three.js frees
// the program when its last material is disposed - so a visualizer that
// comes back in the rotation would compile again on every appearance. The
// visualizer ADOPTS the cached holders and resets in _resetUniforms() what
// update() does not write every frame.
const MATERIALS = new Map();
function materialsFor(tier, debug) {
  const key = tier + '|' + debug;
  let m = MATERIALS.get(key);
  if (m) return m;
  const layout = TIER[tier];
  const shared = {
    uProj: { value: new THREE.Vector4(FALLBACK_META.vanish.x, FALLBACK_META.vanish.y, 1376 / 768, 1 - FALLBACK_META.vanish.y) },
    uFit: { value: new THREE.Vector2(1, 1) },
    uDepth: { value: null },
    uWMax: { value: W_MAX_DEFAULT },
    uTime: { value: 0 },
    uSun: { value: new THREE.Vector3(-0.7, 0.4, 0.3) },
    uWinX: { value: -6.5 },
    uWinZ: { value: new THREE.Vector4(7, 7.8, 7.9, 8.7) },
    uWinY: { value: new THREE.Vector4(1, 2.6, 2.8, 4.3) },
    uWinY2: { value: new THREE.Vector2(4.5, 5.1) },
    uPenumbra: { value: new THREE.Vector2(PENUMBRA_K, PENUMBRA_MIN) },
    uCurtain: { value: new THREE.Vector4(-6.0, 8.6, 10.1, 0.45) },
    uWave: { value: 0 },
    uRoom: { value: new THREE.Vector4(-6.5, 1.5, 10, 5.2) },
    uSunColor: { value: new THREE.Vector3(1, 0.8, 0.5) },
    uSunLevel: { value: 0 }
  };
  const photoUniforms = Object.assign({}, shared, {
    uMap: { value: null },
    uAmbient: { value: AMBIENT },
    uBounce: { value: new THREE.Vector4(0, 0, 5, 0) },
    uBounceColor: { value: new THREE.Vector3(0.6, 0.4, 0.25) },
    uCurtainUV: { value: new THREE.Vector3(0.323, 0.385, 0.705) },
    uVeil: { value: 0 },
    uPixelH: { value: 1 / 720 },
    uDebug: { value: debug },
    uSafeAspect: safeZoneUniforms.uAspect,
    uSafeCenter: safeZoneUniforms.uSafeCenter,
    uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
    uSafeFeather: safeZoneUniforms.uSafeFeather
  });
  const photo = new THREE.ShaderMaterial({
    uniforms: photoUniforms, vertexShader: photoVertexShader, fragmentShader: photoFragmentShader(layout),
    depthTest: false, depthWrite: false
  });
  let dust = null, dustUniforms = null;
  if (layout.dust) {
    dustUniforms = Object.assign({}, shared, { uPixelScale: { value: 1 }, uTwinkle: { value: 0.5 } });
    dust = new THREE.ShaderMaterial({
      uniforms: dustUniforms, vertexShader: dustVertexShader, fragmentShader: dustFragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    });
  }
  let lines = null;
  if (debug === 1) {
    lines = new THREE.ShaderMaterial({ uniforms: { uProj: shared.uProj, uFit: shared.uFit }, vertexShader: lineVertexShader, fragmentShader: lineFragmentShader, depthTest: false, depthWrite: false });
  }
  m = { shared, photoUniforms, photo, dustUniforms, dust, lines };
  MATERIALS.set(key, m);
  return m;
}

export class SunroomVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    this._debug = urlParam('sunroomplanes') === '1' ? 1 : (urlParam('sunroomdepth') === '1' ? 2 : 0);
    const f = parseFloat(urlParam('sunroomsun'));
    this._forcedSun = Number.isFinite(f) ? clamp01(f) : null;
    // ?sunroomcloud=1 holds a cloud in front of the sun: the blurred state;
    // ?sunroomlevel=<f> forces the sun's level (0: the photo alone), for
    // the calibration frames.
    this._forceCloud = urlParam('sunroomcloud') === '1';
    const lv = parseFloat(urlParam('sunroomlevel'));
    this._forcedLevel = Number.isFinite(lv) ? lv : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._time = 0;
    this._paused = false;
    this._meta = FALLBACK_META;
    this._aspectPhoto = FALLBACK_META.sourceSize[0] / FALLBACK_META.sourceSize[1];
    this._ready = false;
    this._pixelH = 1 / 720;
    this._progress = null;
    this._trackChangedAt = Date.now();
    this._midSmooth = 0.3;
    this._highSmooth = 0.3;
    this._cloud = 0;
    this._cloudNext = lerp(CLOUD_GAP[0], CLOUD_GAP[1], Math.random());
    this._cloudUntil = -1;
    this._lastPaletteRef = undefined;
    this._paletteTint = null;
    this._sun = new THREE.Vector3();
    this._sunColor = new THREE.Vector3();
    this._hit = new THREE.Vector3();
    this._patch = { x: 0, y: 0, z: 5, onFloor: true };

    const mats = materialsFor(this._tier, this._debug);
    this._mats = mats;
    this._shared = mats.shared;
    this._photoUniforms = mats.photoUniforms;
    this._resetUniforms();
    this._buildPhoto();
    this._buildDust();
    this._buildLines();
    this._loadScene();
  }

  _resetUniforms() {
    const s = this._shared;
    s.uDepth.value = null;
    s.uTime.value = 0;
    s.uSunLevel.value = 0;
    s.uWave.value = 0;
    this._photoUniforms.uMap.value = null;
    this._photoUniforms.uAmbient.value = AMBIENT;
    this._photoUniforms.uBounce.value.set(0, 0, 5, 0);
    this._photoUniforms.uVeil.value = 0;
    this._photoUniforms.uDebug.value = this._debug;
    if (this._mats.dustUniforms) this._mats.dustUniforms.uTwinkle.value = 0.5;
  }

  // --- the scene, from the manifest ------------------------------------------
  _loadScene() {
    const self = this;
    const want = this._tier >= 2 ? 2560 : 1600;
    fetch(SCENE_MANIFEST)
      .then(function (r) { if (!r.ok) throw new Error('no manifest'); return r.json(); })
      .then(function (m) {
        if (!self._scene) return null;
        if (m.vanish && m.room && m.window) {
          self._meta = {
            vanish: m.vanish, room: m.room, window: m.window,
            curtain: m.curtain || FALLBACK_META.curtain,
            sourceSize: m.sourceSize || FALLBACK_META.sourceSize
          };
        }
        self._aspectPhoto = self._meta.sourceSize[0] / self._meta.sourceSize[1];
        const photo = (m.variants || []).filter(function (v) { return !v.role; })
          .sort(function (a, b) { return Math.abs(a.width - want) - Math.abs(b.width - want); })[0];
        const depth = (m.variants || []).filter(function (v) { return v.role === 'depth'; })[0];
        if (!photo) throw new Error('no photo variant');
        self._wMax = (depth && depth.wMax) || W_MAX_DEFAULT;
        const load = function (f) { return new Promise(function (res, rej) { new THREE.TextureLoader().load('textures/' + f, res, undefined, rej); }); };
        return Promise.all([load(photo.file), depth && self._layout.dust ? load(depth.file).catch(function () { return null; }) : Promise.resolve(null)]);
      })
      .then(function (pair) {
        if (!pair || !self._scene) return;
        const tex = pair[0], dep = pair[1];
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
        // Seen head-on, the photo needs no anisotropy (paid per tap on the
        // Pi); trilinear, because the 1600 variant sits between two mips on
        // the kiosk's 1023-px tier-1 quad and the nearest one blurs it.
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.anisotropy = 1;
        if (dep) {
          dep.colorSpace = THREE.NoColorSpace;
          dep.wrapS = dep.wrapT = THREE.ClampToEdgeWrapping;
          dep.minFilter = THREE.LinearFilter;
          dep.magFilter = THREE.LinearFilter;
          dep.generateMipmaps = false;
        }
        self._photoTex = tex;
        self._depthTex = dep;
        self._shared.uDepth.value = dep;
        self._shared.uWMax.value = self._wMax;
        self._photoUniforms.uMap.value = tex;
        self._placeRoom();
        self._photo.visible = true;
        if (self._dust) self._dust.visible = true;
        if (self._lines) self._lines.visible = true;
        self._ready = true;
      })
      .catch(function (err) {
        if (!self._scene) return;
        console.warn('[sunroom] no room photo (' + (err && err.message) + '): nothing to light - run tools/build-scenes.mjs');
      });
  }

  // THE ROOM AND THE WINDOW, from the manifest's image measurements. The
  // glass corners are unprojected onto the left wall: z from the column
  // (u), y from the row at that z. The mullion's width and the rails'
  // thickness become gaps in the pane grid.
  _placeRoom() {
    const m = this._meta, A = this._aspectPhoto, vx = m.vanish.x, vy = m.vanish.y, hc = 1 - vy;
    const room = m.room, win = m.window;
    const zOf = (u) => room.left / (A * (u - vx));
    const yOf = (v, z) => (vy - v) * z;
    const [TL, TR, BR, BL] = win.corners;
    const z0 = zOf(TL[0]), z1 = zOf(TR[0]);
    const yBottom = 0.5 * (yOf(BL[1], z0) + yOf(BR[1], z1));
    const yTop = 0.5 * (yOf(TL[1], z0) + yOf(TR[1], z1));
    const H = yTop - yBottom;
    const zm = zOf(win.mullion.u);
    const dzdu = Math.abs(room.left / (A * (win.mullion.u - vx) * (win.mullion.u - vx)));
    const hm = dzdu * (win.mullion.widthPx / (2 * m.sourceSize[0]));
    const yr = yBottom + win.rail.frac * H, hr = 0.5 * win.rail.thick * H;
    const barTop = yTop + win.barThick * H;
    const y1 = barTop + win.topRow;
    const s = this._shared;
    s.uProj.value.set(vx, vy, A, hc);
    s.uRoom.value.set(room.left, room.right, room.back, room.ceiling);
    s.uWinX.value = room.left;
    s.uWinZ.value.set(z0, zm - hm, zm + hm, z1);
    s.uWinY.value.set(yBottom, yr - hr, yr + hr, yTop);
    s.uWinY2.value.set(barTop, y1);
    const c = m.curtain;
    s.uCurtain.value.set(room.left + c.inset, c.z0, c.z1, c.transmission);
    this._photoUniforms.uCurtainUV.value.set(c.u0, c.u1, c.v1);
    this._winCenter = new THREE.Vector3(room.left, 0.5 * (yBottom + y1), 0.5 * (z0 + z1));
    this._win = { z0, z1, zm, hm, yBottom, yTop, yr, hr, barTop, y1 };
    if (this._lines) this._fillLines();
  }

  _buildPhoto() {
    this._photo = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mats.photo);
    this._photo.frustumCulled = false;
    this._photo.renderOrder = -1000;
    this._photo.visible = false;
    const self = this;
    this._photo.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._fit(_size.x, _size.y);
    };
    this._scene.add(this._photo);
  }

  // The cover fit: all of the photo's width on a frame wider than it (the
  // kiosk's 3.2:1 shows rows 0.22 to 0.78), all of its height on a narrower one.
  _fit(wpx, hpx) {
    const af = wpx / Math.max(1, hpx), ap = this._aspectPhoto;
    const fit = this._shared.uFit.value;
    if (af >= ap) fit.set(1, ap / af); else fit.set(af / ap, 1);
    this._pixelH = fit.y / Math.max(1, hpx);
    this._photoUniforms.uPixelH.value = this._pixelH;
    if (this._mats.dustUniforms) this._mats.dustUniforms.uPixelScale.value = hpx / 720;
  }

  _buildDust() {
    this._dust = null;
    const n = this._layout.dust;
    if (!n || !this._mats.dust) return;
    let geo = this._mats.dustGeo;
    if (!geo) {
      const pos = new Float32Array(n * 3), seed = new Float32Array(n * 4);
      for (let i = 0; i < n; i++) {
        seed[i * 4] = Math.random(); seed[i * 4 + 1] = Math.random(); seed[i * 4 + 2] = Math.random(); seed[i * 4 + 3] = Math.random();
      }
      geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 4));
      geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 100);
      this._mats.dustGeo = geo;
    }
    this._dust = new THREE.Points(geo, this._mats.dust);
    this._dust.frustumCulled = false;
    this._dust.renderOrder = -999;
    this._dust.visible = false;
    this._scene.add(this._dust);
  }

  _buildLines() {
    this._lines = null;
    if (!this._mats.lines) return;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3 * 2 * 40), 3));
    geo.setAttribute('aColor', new THREE.BufferAttribute(new Float32Array(3 * 2 * 40), 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 100);
    this._linesGeo = geo;
    this._lines = new THREE.LineSegments(geo, this._mats.lines);
    this._lines.frustumCulled = false;
    this._lines.renderOrder = 10;
    this._lines.visible = false;
    this._scene.add(this._lines);
  }

  _fillLines() {
    const r = this._meta.room, hc = 1 - this._meta.vanish.y, w = this._win;
    const segs = [];
    const add = (a, b, c) => segs.push([a, b, c]);
    const F = -hc, C = r.ceiling, zn = 0.6;
    const blue = [0.2, 0.5, 1.0], red = [1.0, 0.15, 0.15], green = [0.2, 1.0, 0.3], yellow = [1.0, 0.9, 0.2];
    // floor edges and ceiling edges of the box, the corner verticals
    add([r.left, F, zn], [r.left, F, r.back], blue); add([r.right, F, zn], [r.right, F, r.back], blue);
    add([r.left, F, r.back], [r.right, F, r.back], blue);
    add([r.left, C, zn], [r.left, C, r.back], blue); add([r.right, C, zn], [r.right, C, r.back], blue);
    add([r.left, C, r.back], [r.right, C, r.back], blue);
    add([r.left, F, r.back], [r.left, C, r.back], blue); add([r.right, F, r.back], [r.right, C, r.back], blue);
    // the window: columns, rows, the bars
    const x = r.left + 0.01;
    for (const z of [w.z0, w.zm - w.hm, w.zm + w.hm, w.z1]) add([x, w.yBottom, z], [x, w.y1, z], red);
    for (const y of [w.yBottom, w.yr - w.hr, w.yr + w.hr, w.yTop, w.barTop, w.y1]) add([x, y, w.z0], [x, y, w.z1], red);
    // the voile's plane
    const c = this._shared.uCurtain.value;
    add([c.x, F, c.y], [c.x, C, c.y], green); add([c.x, F, c.z], [c.x, C, c.z], green); add([c.x, C, c.y], [c.x, C, c.z], green);
    // the sun's ray through the window's centre (refreshed in update)
    this._sunSegIndex = segs.length;
    add([x, 2, 8], [x, 2, 8], yellow);
    const pos = this._linesGeo.getAttribute('position'), col = this._linesGeo.getAttribute('aColor');
    for (let i = 0; i < segs.length && i < 40; i++) {
      pos.setXYZ(i * 2, segs[i][0][0], segs[i][0][1], segs[i][0][2]); pos.setXYZ(i * 2 + 1, segs[i][1][0], segs[i][1][1], segs[i][1][2]);
      col.setXYZ(i * 2, segs[i][2][0], segs[i][2][1], segs[i][2][2]); col.setXYZ(i * 2 + 1, segs[i][2][0], segs[i][2][1], segs[i][2][2]);
    }
    this._linesGeo.setDrawRange(0, segs.length * 2);
    pos.needsUpdate = true; col.needsUpdate = true;
  }

  onTrackChange(profile) {
    this._progress = null;             // snap, never glide from the last track's end
    this._trackChangedAt = Date.now();
  }

  // --- the sun -------------------------------------------------------------------
  // Where the ray through the window's centre lands: the patch's centre, for
  // the bounce and the debug line.
  _patchCentre(S, out) {
    const r = this._meta.room, hc = 1 - this._meta.vanish.y, W = this._winCenter;
    let t = (W.y + hc) / Math.max(S.y, 0.02), onFloor = true;
    if (S.z < -0.001) { const tb = (W.z - r.back) / S.z; if (tb < t) { t = tb; onFloor = false; } }
    const tr = (W.x - r.right) / S.x; if (tr < t) { t = tr; onFloor = false; }
    if (S.z > 0.001) { const tn = (W.z - 0.6) / S.z; if (tn < t) { t = tn; } }
    out.set(W.x - S.x * t, W.y - S.y * t, W.z - S.z * t);
    return onFloor;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;

    // --- progress: the one driver ---------------------------------------------
    let target;
    if (this._forcedSun != null) target = this._forcedSun;
    else if (profile.duration > 0) target = clamp01(profile.progress || 0);
    else target = ((Date.now() - (profile.trackChangedAt || this._trackChangedAt)) / 1000 / RADIO_LOOP_SECONDS) % 1;
    if (this._progress == null || Math.abs(target - this._progress) > 0.5) this._progress = target;
    else this._progress += (target - this._progress) * (1 - Math.exp(-dt / PROGRESS_TAU));
    const p = this._progress;

    // --- the bands: the voile and the dust, nothing else -------------------------
    const director = profile.director || {};
    const bands = director.bands || null;
    const has = !!(bands && director.hasReliableGrid);
    const kb = 1 - Math.exp(-dt / 0.6);
    this._midSmooth += ((has ? clamp01(bands.mid) : 0.4 + 0.3 * Math.sin(this._time * 0.21)) - this._midSmooth) * kb;
    this._highSmooth += ((has ? clamp01(bands.high) : 0.4 + 0.3 * Math.sin(this._time * 0.17 + 1.0)) - this._highSmooth) * kb;

    // --- palette: a fifth of the light's hue ---------------------------------------
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const dom = pal && pal.dominantColors && pal.dominantColors.length ? pal.dominantColors[0] : null;
      if (dom != null) {
        const c = new THREE.Color(dom); c.getHSL(_hsl);
        // a warm, light tint of the dominant hue, in LINEAR light
        c.setHSL(_hsl.h, Math.min(0.6, _hsl.s), 0.6);
        this._paletteTint = [srgbToLinear(c.r), srgbToLinear(c.g), srgbToLinear(c.b)];
      } else this._paletteTint = null;
    }

    // --- clouds ---------------------------------------------------------------------
    let cloudTarget = 0;
    if (this._forceCloud) cloudTarget = 1;
    else if (this._time < this._cloudUntil) cloudTarget = 1;
    else if (this._time >= this._cloudNext) {
      this._cloudUntil = this._time + lerp(CLOUD_HOLD[0], CLOUD_HOLD[1], Math.random());
      this._cloudNext = this._cloudUntil + lerp(CLOUD_GAP[0], CLOUD_GAP[1], Math.random());
      cloudTarget = 1;
    }
    this._cloud += (cloudTarget - this._cloud) * (1 - Math.exp(-dt / CLOUD_RAMP));

    // --- the sun ----------------------------------------------------------------------
    const az = pathAt(p, 1) * Math.PI / 180, el = pathAt(p, 2) * Math.PI / 180;
    const level = (this._forcedLevel != null ? this._forcedLevel : clamp01(pathAt(p, 3))) * (1 - (1 - CLOUD_LEVEL) * this._cloud);
    const S = this._sun.set(-Math.cos(el) * Math.cos(az), Math.sin(el), Math.cos(el) * Math.sin(az)).normalize();
    const wDawn = 1 - smooth01(p / 0.16), wLate = smooth01((p - 0.62) / 0.30);
    const col = this._sunColor.set(
      lerp(lerp(SUN_GOLD[0], SUN_DAWN[0], wDawn), SUN_LATE[0], wLate),
      lerp(lerp(SUN_GOLD[1], SUN_DAWN[1], wDawn), SUN_LATE[1], wLate),
      lerp(lerp(SUN_GOLD[2], SUN_DAWN[2], wDawn), SUN_LATE[2], wLate));
    if (this._paletteTint) {
      col.x = lerp(col.x, col.x * this._paletteTint[0] / 0.6, PALETTE_TINT);
      col.y = lerp(col.y, col.y * this._paletteTint[1] / 0.6, PALETTE_TINT);
      col.z = lerp(col.z, col.z * this._paletteTint[2] / 0.6, PALETTE_TINT);
    }

    const s = this._shared;
    s.uTime.value = this._time % 1000;
    s.uSun.value.copy(S);
    s.uSunLevel.value = level * SUN_GAIN;
    s.uSunColor.value.copy(col);
    const blur = 1 + CLOUD_BLUR * this._cloud;
    if (this._layout.penumbra === 'variable') s.uPenumbra.value.set(PENUMBRA_K * blur, PENUMBRA_MIN * blur);
    else s.uPenumbra.value.set(0, PENUMBRA_FIXED * blur);
    s.uWave.value = this._layout.veil ? 0.10 * (0.5 + this._midSmooth) : 0.06;
    this._photoUniforms.uVeil.value = this._layout.veil ? VEIL_PX * (0.4 + 0.6 * this._midSmooth) * this._pixelH : 0;
    if (this._mats.dustUniforms) this._mats.dustUniforms.uTwinkle.value = 0.3 + 0.7 * this._highSmooth;

    if (this._ready) {
      const onFloor = this._patchCentre(S, this._hit);
      this._patch.x = this._hit.x; this._patch.y = this._hit.y; this._patch.z = this._hit.z; this._patch.onFloor = onFloor;
      if (this._layout.bounce) {
        const tint = onFloor ? FLOOR_TINT : WALL_TINT;
        this._photoUniforms.uBounce.value.set(this._hit.x, this._hit.y, this._hit.z, level * BOUNCE_GAIN);
        this._photoUniforms.uBounceColor.value.set(col.x * tint[0], col.y * tint[1], col.z * tint[2]);
      }
      if (this._lines && this._sunSegIndex != null) {
        const pos = this._linesGeo.getAttribute('position'), W = this._winCenter;
        pos.setXYZ(this._sunSegIndex * 2, W.x + 0.01, W.y, W.z);
        pos.setXYZ(this._sunSegIndex * 2 + 1, this._hit.x, this._hit.y, this._hit.z);
        pos.needsUpdate = true;
      }
    }
  }

  // For probes: the state the acceptance criteria are stated on.
  get probe() {
    return {
      progress: this._progress, sun: [this._sun.x, this._sun.y, this._sun.z], level: this._shared.uSunLevel.value / SUN_GAIN,
      cloud: this._cloud, patch: Object.assign({}, this._patch), ready: this._ready, tier: this._tier,
      window: this._win || null, penumbra: [this._shared.uPenumbra.value.x, this._shared.uPenumbra.value.y]
    };
  }

  dispose() {
    this._scene.remove(this._photo);
    this._photo.geometry.dispose();
    if (this._dust) this._scene.remove(this._dust);
    if (this._lines) { this._scene.remove(this._lines); this._linesGeo.dispose(); }
    if (this._photoTex) { this._photoTex.dispose(); this._photoUniforms.uMap.value = null; }
    if (this._depthTex) { this._depthTex.dispose(); this._shared.uDepth.value = null; }
    this._photoTex = null;
    this._depthTex = null;
    this._scene = null;
  }
}

function smooth01(x) { x = clamp01(x); return x * x * (3 - 2 * x); }
function srgbToLinear(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
const _size = new THREE.Vector2();
const _hsl = { h: 0, s: 0, l: 0 };
